import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('valnexo_token'));
  const [loading, setLoading] = useState(true);

  const axiosInstance = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  // Update axios headers when token changes
  useEffect(() => {
    if (token) {
      axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.Authorization;
    }
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('valnexo_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('valnexo_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role = 'trainer') => {
    const response = await axios.post(`${API}/auth/register`, { 
      name, email, password, role 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('valnexo_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('valnexo_token');
    setToken(null);
    setUser(null);
  };

  const api = {
    get: (url, config = {}) => axios.get(`${API}${url}`, { 
      ...config, 
      headers: { ...config.headers, Authorization: `Bearer ${token}` } 
    }),
    post: (url, data, config = {}) => axios.post(`${API}${url}`, data, { 
      ...config, 
      headers: { ...config.headers, Authorization: `Bearer ${token}` } 
    }),
    put: (url, data, config = {}) => axios.put(`${API}${url}`, data, { 
      ...config, 
      headers: { ...config.headers, Authorization: `Bearer ${token}` } 
    }),
    delete: (url, config = {}) => axios.delete(`${API}${url}`, { 
      ...config, 
      headers: { ...config.headers, Authorization: `Bearer ${token}` } 
    }),
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, api }}>
      {children}
    </AuthContext.Provider>
  );
};
